import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import TreeShadowDiagram from "../../../../components/diagrams/TreeShadowDiagram";
import FractionInput from "../../../../components/inputs/FractionInput";

function randomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function generateTask() {
  const angle = randomInt(20, 45); // 🎯 losowany kąt
  const base = randomInt(3, 15);   // długość cienia
  const radians = (angle * Math.PI) / 180;
  const height = Number((base * Math.tan(radians)).toFixed(2));

  return {
    type: "TreeShadowTask",
    angle,
    base,
    height,
    data: { angle, base, height }
  };
}

export function renderTask({ angle, base }) {
  return (
    <div className="space-y-2">
      <p>
        Cień drzewa ma długość <InlineMath math={`${base}\\ \\text{m}`} inline />, a promienie słoneczne padają na ziemię pod kątem{" "}
        <InlineMath math={`${angle}^\\circ`} inline />.
        <br />
        Oblicz wysokość drzewa z dokładnością do <InlineMath math={`0{,}01\\ \\text{m}`} inline />.
      </p>
      {/* 📌 Na rysunku zawsze pokazujemy 35°, niezależnie od treści */}
      <TreeShadowDiagram angle={angle} shadowLength={base} height={null} />

    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label="h"
      suffixLatex="\text{m}"
    />
  );
}

export function renderAnswer({ height }) {
  return <InlineMath math={`${height.toFixed(2)}\\ \\text{m}`} />;
}

export function checkAnswer(data, userAnswer) {
  const expected = data.height;
  const given = userAnswer?.parsed ?? Number(userAnswer);
  if (!isFinite(given)) return false;
  return Math.abs(given - expected) <= 0.02;
}

export function renderExplanation(input) {
  const data = input?.data ?? input;
  const { angle, base, height } = data;

  const tan = Math.tan((angle * Math.PI) / 180).toFixed(4);
  const raw = (base * Math.tan((angle * Math.PI) / 180)).toFixed(4);
  const rounded = height.toFixed(2);

  return (
    <div className="space-y-4 text-left">
      <div>
        <strong>Krok 1:</strong> Zapisujemy związek między podstawą a wysokością:
        <BlockMath math={`\\tan(${angle}^\\circ) = \\frac{h}{${base}}`} />
      </div>
      <div>
        <strong>Krok 2:</strong> Przekształcamy wzór:
        <BlockMath math={`h = ${base} \\cdot \\tan(${angle}^\\circ)`} />
      </div>
      <div>
        <strong>Krok 3:</strong> Podstawiamy wartość tangensa:
        <BlockMath math={`h \\approx ${base} \\cdot ${tan} = ${raw}`} />
      </div>
      <div>
        <strong>Krok 4:</strong> Zaokrąglamy wynik:
        <BlockMath math={`h \\approx ${rounded}\\ \\text{m}`} />
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }

  const formatted = userAnswer.raw
    .replaceAll("*", "\\cdot ")
    .replaceAll("/", "\\div ")
    .replaceAll("sqrt", "\\sqrt")
    .replaceAll("-", " - ")
    .replaceAll("+", " + ");

  return <InlineMath math={`${formatted}\\ \\text{m}`} />;
}

export default {
  type: "TreeShadowTask",
  generateTask,
  renderTask,
  renderInput,
  renderAnswer,
  renderExplanation,
  checkAnswer,
  renderUserAnswer
};
